import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FillTarget, useTranslationFillRun } from '../../lib/use-translation-fill-run.js';

// #111: Dialog für „Alle N ergänzen" — Rückfrage → Fortschritt (mit Abbruch) → Zusammenfassung mit
// den fehlgeschlagenen Testfällen. Struktur und Escape-Handling wie confirm-dialog.tsx (zentriertes
// <dialog open aria-modal>, Escape in der CAPTURE-Phase mit stopPropagation, damit ein
// darunterliegendes SidePanel nicht zusätzlich schließt).
// „Schließen" während des Laufs heißt Abbrechen: der Dialog bleibt dann stehen und zeigt die
// Zusammenfassung, sobald der gerade laufende Aufruf zurück ist — ein stilles Wegklicken ließe
// einen Lauf ohne Anzeige zurück (der Hook bricht beim Unmount ohnehin ab).
export function TranslationFillDialog({
  projectId,
  targets,
  onClose,
  onOpenTestCase,
}: {
  projectId: string;
  targets: FillTarget[];
  onClose: () => void;
  onOpenTestCase: (testCaseId: string) => void;
}) {
  const { t } = useTranslation();
  const run = useTranslationFillRun(projectId);
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Abbruch ist angefordert, der laufende Aufruf aber noch nicht zurück → Knopf sperren.
  const [cancelling, setCancelling] = useState(false);

  const running = run.status === 'running';
  const finished = run.status === 'done' || run.status === 'cancelled';

  const requestCancel = () => {
    setCancelling(true);
    run.cancel();
  };
  const dismiss = () => {
    if (running) requestCancel();
    else onClose();
  };
  // Der Listener liest `dismiss` über eine Ref, damit er nicht bei jedem Fortschrittsschritt
  // neu am document hängt.
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      dismissRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // Ohne Optionen ohne zweites Argument aufrufen (exactOptionalPropertyTypes verbietet `undefined`).
  const label = (key: string, opts?: Record<string, unknown>) =>
    opts
      ? t(`catalog.translationFill.bulk.${key}`, opts)
      : t(`catalog.translationFill.bulk.${key}`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={dismiss}
        className="absolute inset-0"
      />
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={t('catalog.translationFill.action')}
        className="relative z-10 m-0 w-full max-w-md space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl outline-none"
      >
        <h2 className="text-sm font-semibold text-text-primary">
          {t('catalog.translationFill.action')}
        </h2>

        {run.status === 'idle' && (
          <>
            <p className="text-sm text-text-muted">{label('confirm', { count: targets.length })}</p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => run.start(targets)}
                className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
              >
                {label('start')}
              </button>
            </div>
          </>
        )}

        {running && (
          <>
            {/* #111: kein aria-live auf dem Zähler — jede Zahl wäre eine Ansage; angesagt wird der Abschluss. */}
            <p className="text-sm text-text-body">
              {label('progress', { done: run.done, total: run.total })}
            </p>
            <progress
              value={run.done}
              max={run.total}
              aria-label={label('progressLabel')}
              className="h-2 w-full"
            />
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                disabled={cancelling}
                onClick={requestCancel}
                className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body disabled:opacity-60"
              >
                {cancelling ? label('cancelling') : t('common.cancel')}
              </button>
            </div>
          </>
        )}

        {finished && (
          <>
            {/* Der Statusbereich erscheint erst mit dem Ende des Laufs → aria-live sagt ihn einmal an. */}
            <output className="block space-y-1" aria-live="polite">
              <p className="text-sm text-text-body">
                {run.unchanged.length > 0
                  ? label('summaryWithUnchanged', {
                      succeeded: run.succeeded,
                      count: run.unchanged.length,
                      failed: run.failed.length,
                    })
                  : label('summary', { succeeded: run.succeeded, failed: run.failed.length })}
                {/* #160: Teilweise ergänzt zählt weder als ergänzt noch als fehlgeschlagen. */}
                {run.partial.length > 0 && (
                  <span className="block">
                    {label('summaryPartial', { count: run.partial.length })}
                  </span>
                )}
                {run.status === 'cancelled' && (
                  <span className="block text-text-muted">
                    {label('summaryCancelled', { done: run.done, total: run.total })}
                  </span>
                )}
              </p>
              {run.stopReason && (
                <p className="text-sm text-status-fail">{label(`stopped.${run.stopReason}`)}</p>
              )}
            </output>
            <TargetList
              heading={label('unchangedHeading')}
              targets={run.unchanged}
              onOpenTestCase={onOpenTestCase}
            />
            <TargetList
              heading={label('partialHeading')}
              targets={run.partial}
              onOpenTestCase={onOpenTestCase}
            />
            <TargetList
              heading={label('failedHeading')}
              targets={run.failed}
              onOpenTestCase={onOpenTestCase}
            />
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
              >
                {t('common.close')}
              </button>
            </div>
          </>
        )}
      </dialog>
    </div>
  );
}

// Testfälle eines Ergebnistopfs als Links zum Öffnen; ohne Einträge nichts.
function TargetList({
  heading,
  targets,
  onOpenTestCase,
}: {
  heading: string;
  targets: FillTarget[];
  onOpenTestCase: (testCaseId: string) => void;
}) {
  if (targets.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs text-text-muted">{heading}</p>
      {/* Bis zu einige Hundert Einträge möglich → scrollbar statt den Dialog zu sprengen. */}
      <ul className="flex max-h-40 flex-wrap gap-1 overflow-y-auto">
        {targets.map((f) => (
          <li key={f.test_case_id}>
            <button
              type="button"
              onClick={() => onOpenTestCase(f.test_case_id)}
              className="rounded-btn border border-[var(--border)] px-2 py-0.5 text-xs text-brand-accent hover:bg-brand-accent/10"
            >
              {f.human_id}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
